import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Chip,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";

const RepeatSchedulePicker = ({
  repeatCount,
  value = [],
  onChange,
  error = false,
  helperText = "",
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('forms');

  // Parse schedule items: "1w" -> { number: 1, unit: "w" }
  const parseScheduleItem = (item) => {
    if (!item) return { number: "", unit: "w" };
    const match = item.match(/^(\d+)([wmdy])$/);
    if (match) {
      return { number: match[1], unit: match[2] };
    }
    return { number: "", unit: "w" };
  };

  // Format schedule item: { number: 1, unit: "w" } -> "1w"
  const formatScheduleItem = (number, unit) => {
    if (!number || number === "") return "";
    return `${number}${unit}`;
  };

  // Initialize schedule items from value prop
  const [scheduleItems, setScheduleItems] = useState(() => {
    const count = parseInt(repeatCount) || 0;
    const neededCount = count > 1 ? count - 1 : 0;
    
    if (value && value.length === neededCount) {
      return value.map(parseScheduleItem);
    }
    
    // Initialize with empty items
    return Array.from({ length: neededCount }, () => ({ number: "", unit: "w" }));
  });

  // Update schedule items when repeatCount changes
  useEffect(() => {
    const count = parseInt(repeatCount) || 0;
    const neededCount = count > 1 ? count - 1 : 0;
    
    if (neededCount === 0) {
      setScheduleItems([]);
      onChange([]);
      return;
    }

    // If current items count doesn't match needed count, adjust
    setScheduleItems((prevItems) => {
      if (prevItems.length === neededCount) {
        return prevItems; // No change needed
      }
      
      const newItems = [];
      for (let i = 0; i < neededCount; i++) {
        if (i < prevItems.length) {
          newItems.push(prevItems[i]);
        } else {
          newItems.push({ number: "", unit: "w" });
        }
      }
      return newItems;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatCount]);

  // Update parent when schedule items change
  useEffect(() => {
    const schedule = scheduleItems.map((item) => formatScheduleItem(item.number, item.unit));
    onChange(schedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleItems]);

  const handleNumberChange = (index, newNumber) => {
    const updated = [...scheduleItems];
    updated[index] = { ...updated[index], number: newNumber.replace(/\D/g, "") };
    setScheduleItems(updated);
  };

  const handleUnitChange = (index, newUnit) => {
    const updated = [...scheduleItems];
    updated[index] = { ...updated[index], unit: newUnit };
    setScheduleItems(updated);
  };

  // Validate schedule: check if values are in ascending order
  const validateSchedule = () => {
    const parseIntervalToDays = (item) => {
      if (!item || !item.number) return 0;
      const num = parseFloat(item.number);
      if (isNaN(num)) return 0;
      
      switch (item.unit) {
        case "w": return num * 7;
        case "m": return num * 30;
        case "d": return num;
        case "y": return num * 365;
        default: return 0;
      }
    };

    let prev = 0;
    for (let i = 0; i < scheduleItems.length; i++) {
      const item = scheduleItems[i];
      if (!item.number || item.number === "") {
        return { valid: false, message: t('repeatScheduleSendRequiresValue', { number: i + 2 }) };
      }
      
      const days = parseIntervalToDays(item);
      if (days <= 0) {
        return { valid: false, message: t('repeatScheduleSendMustBePositive', { number: i + 2 }) };
      }
      
      if (days <= prev) {
        return { valid: false, message: t('repeatScheduleSendMustBeAfter', { number: i + 2, previousNumber: i + 1 }) };
      }
      
      prev = days;
    }
    
    return { valid: true, message: "" };
  };

  const validation = validateSchedule();
  const hasError = error || !validation.valid;

  const count = parseInt(repeatCount) || 0;
  if (count <= 1) {
    return null;
  }

  const unitLabels = {
    d: t('days'),
    w: t('weeks'),
    m: t('months'),
    y: t('years'),
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.primary[600],
        border: `2px solid ${hasError ? colors.redAccent[500] : colors.blueAccent[500]}`,
        boxShadow: `0 4px 16px ${hasError ? colors.redAccent[900] : colors.blueAccent[900]}40`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <ScheduleIcon
          sx={{
            fontSize: 32,
            color: colors.blueAccent[400],
          }}
        />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.grey[100],
              mb: 0.5,
            }}
          >
            {t('repeatScheduleConfiguration')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.grey[300],
              fontSize: "0.85rem",
            }}
          >
            {t('repeatScheduleDescription')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {scheduleItems.map((item, index) => {
          const sendNumber = index + 2; // Send 2, Send 3, etc.
          
          return (
            <Box
              key={index}
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: colors.primary[500],
                border: `1px solid ${colors.primary[300]}`,
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: colors.blueAccent[400],
                  backgroundColor: colors.primary[400],
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={t('repeatScheduleSendNumber', { number: sendNumber })}
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontWeight: 600,
                    minWidth: 80,
                  }}
                />
                
                <Typography
                  sx={{
                    color: colors.grey[300],
                    fontWeight: 500,
                  }}
                >
                  {t('after')}
                </Typography>

                <TextField
                  type="number"
                  value={item.number}
                  onChange={(e) => handleNumberChange(index, e.target.value)}
                  placeholder="0"
                  variant="outlined"
                  size="small"
                  inputProps={{
                    min: 1,
                    max: 999,
                    style: { textAlign: "center" },
                  }}
                  sx={{
                    width: 100,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.primary[600],
                      border: `2px solid ${colors.blueAccent[600]}`,
                      "&:hover": {
                        borderColor: colors.blueAccent[500],
                        backgroundColor: colors.primary[500],
                      },
                      "&.Mui-focused": {
                        borderColor: colors.blueAccent[400],
                        backgroundColor: colors.primary[400],
                        boxShadow: `0 0 0 3px ${colors.blueAccent[900]}`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      color: colors.grey[100],
                      fontWeight: "bold",
                    },
                    "& input[type=number]": {
                      "-moz-appearance": "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                  }}
                />

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 140,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.primary[600],
                      border: `2px solid ${colors.greenAccent[600]}`,
                      "&:hover": {
                        borderColor: colors.greenAccent[500],
                        backgroundColor: colors.primary[500],
                      },
                      "&.Mui-focused": {
                        borderColor: colors.greenAccent[400],
                        backgroundColor: colors.primary[400],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[900]}`,
                      },
                    },
                  }}
                >
                  <Select
                    value={item.unit}
                    onChange={(e) => handleUnitChange(index, e.target.value)}
                    IconComponent={ExpandMoreIcon}
                    sx={{
                      borderRadius: 2,
                      "& .MuiSelect-select": {
                        padding: "10px 14px",
                        color: colors.grey[100],
                        fontWeight: 600,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 2,
                          mt: 1,
                          backgroundColor: colors.primary[400],
                          "& .MuiMenuItem-root": {
                            px: 2,
                            py: 1.5,
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: colors.greenAccent[700],
                              color: "white",
                            },
                            "&.Mui-selected": {
                              backgroundColor: colors.greenAccent[800],
                              "&:hover": {
                                backgroundColor: colors.greenAccent[700],
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="d">{unitLabels.d}</MenuItem>
                    <MenuItem value="w">{unitLabels.w}</MenuItem>
                    <MenuItem value="m">{unitLabels.m}</MenuItem>
                    <MenuItem value="y">{unitLabels.y}</MenuItem>
                  </Select>
                </FormControl>

                <Typography
                  sx={{
                    color: colors.grey[400],
                    fontSize: "0.85rem",
                    fontStyle: "italic",
                  }}
                >
                  {t('fromTreatmentStartDate')}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {hasError && (
        <Typography
          variant="caption"
          sx={{
            color: colors.redAccent[400],
            mt: 2,
            display: "block",
            fontSize: "0.85rem",
          }}
        >
          {helperText || validation.message || t('pleaseConfigureAllSendSchedules')}
        </Typography>
      )}

      {!hasError && scheduleItems.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.greenAccent[900],
            border: `1px solid ${colors.greenAccent[600]}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.greenAccent[300],
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {t('scheduleIsValid')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RepeatSchedulePicker;

